import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Editor } from '@tiptap/react';
import { X, Search, Replace, GripVertical, Loader2 } from 'lucide-react';
import { setFindReplaceMatches, clearFindReplaceMatches } from './FindReplaceHighlightExtension';
import './FindReplaceDialog.css';

interface FindReplaceDialogProps {
    editor: Editor | null;
    isOpen: boolean;
    onClose: () => void;
}

interface SearchResult {
    from: number;
    to: number;
    text: string;
}

const FindReplaceDialog: React.FC<FindReplaceDialogProps> = ({
    editor,
    isOpen,
    onClose
}) => {
    const [findText, setFindText] = useState('');
    const [replaceText, setReplaceText] = useState('');
    const [caseSensitive, setCaseSensitive] = useState(false);
    const [useRegex, setUseRegex] = useState(false);
    const [regexError, setRegexError] = useState<string | null>(null);
    const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);
    const [matches, setMatches] = useState<SearchResult[]>([]);
    const [isReplacingAll, setIsReplacingAll] = useState(false);
    const isReplacingAllRef = useRef(false); // Synchronous flag to prevent concurrent operations
    const findInputRef = useRef<HTMLInputElement>(null);
    const replaceInputRef = useRef<HTMLInputElement>(null);
    const dialogRef = useRef<HTMLDivElement>(null);
    const isDraggingRef = useRef(false);
    const dragStartPosRef = useRef({ x: 0, y: 0 });
    const dialogPosRef = useRef({ x: 0, y: 0 });
    const scrollTimeoutRef = useRef<number | null>(null);

    // Focus find input when dialog opens
    useEffect(() => {
        if (isOpen && findInputRef.current) {
            setTimeout(() => {
                findInputRef.current?.focus();
            }, 100);
        }
        // Note: We don't clear selection when dialog closes to avoid unwanted scrolling
        // The selection will remain on the last match, which is fine
    }, [isOpen]);

    // Find all matches in the editor
    const findMatches = useCallback((text: string): SearchResult[] => {
        if (!editor || !text.trim()) {
            setRegexError(null);
            return [];
        }

        const results: SearchResult[] = [];
        const doc = editor.state.doc;

        // Build full text and character-to-position mapping
        // Use the same approach as SearchHighlightExtension but handle cross-node matches
        const textNodes: Array<{ text: string; pos: number; charStart: number }> = [];
        let charCount = 0;
        
        doc.descendants((node: any, pos: number) => {
            if (node.isText && node.text) {
                textNodes.push({
                    text: node.text,
                    pos: pos, // In descendants, pos for text nodes is the position of the first character
                    charStart: charCount
                });
                charCount += node.text.length;
            }
        });

        const fullText = textNodes.map(tn => tn.text).join('');

        // Convert character index to ProseMirror position
        // Based on SearchHighlightExtension: pos is already the first character position
        const charToPos = (charIdx: number): number | null => {
            for (const tn of textNodes) {
                const end = tn.charStart + tn.text.length;
                if (charIdx >= tn.charStart && charIdx < end) {
                    const offset = charIdx - tn.charStart;
                    return tn.pos + offset;
                }
            }
            return null;
        };

        if (useRegex) {
            try {
                const flags = caseSensitive ? 'g' : 'gi';
                const regex = new RegExp(text, flags);
                setRegexError(null);

                let match;
                while ((match = regex.exec(fullText)) !== null) {
                    const startIdx = match.index;
                    const matchText = match[0];
                    const matchLen = matchText.length;
                    
                    const from = charToPos(startIdx);
                    const to = charToPos(startIdx + matchLen - 1);
                    
                    if (from !== null && to !== null) {
                        // to should be position AFTER last character
                        if (from >= 0 && to + 1 <= doc.content.size && from < to + 1) {
                            results.push({
                                from,
                                to: to + 1,
                                text: matchText
                            });
                        }
                    }

                    if (match[0].length === 0) {
                        regex.lastIndex++;
                    }
                }
            } catch (error) {
                setRegexError(error instanceof Error ? error.message : 'Invalid regex pattern');
                return [];
            }
        } else {
            const searchText = text;
            setRegexError(null);

            const compareIgnoreCase = (str1: string, str2: string): boolean => {
                const isASCII = (s: string) => {
                    for (let i = 0; i < s.length; i++) {
                        if (s.charCodeAt(i) > 127) return false;
                    }
                    return true;
                };
                
                if (isASCII(str1) && isASCII(str2)) {
                    return str1.toLowerCase() === str2.toLowerCase();
                }
                return str1.localeCompare(str2, undefined, { sensitivity: 'base' }) === 0;
            };

            if (caseSensitive) {
                let startIdx = 0;
                while (true) {
                    const charIdx = fullText.indexOf(searchText, startIdx);
                    if (charIdx === -1) break;

                    const from = charToPos(charIdx);
                    const to = charToPos(charIdx + searchText.length - 1);
                    
                    if (from !== null && to !== null) {
                        if (from >= 0 && to + 1 <= doc.content.size && from < to + 1) {
                            results.push({
                                from,
                                to: to + 1,
                                text: fullText.substring(charIdx, charIdx + searchText.length)
                            });
                        }
                    }

                    startIdx = charIdx + 1;
                }
            } else {
                const searchLen = searchText.length;
                const textLen = fullText.length;
                
                for (let i = 0; i <= textLen - searchLen; i++) {
                    const candidate = fullText.substring(i, i + searchLen);
                    
                    if (compareIgnoreCase(candidate, searchText)) {
                        const from = charToPos(i);
                        const to = charToPos(i + candidate.length - 1);
                        
                        if (from !== null && to !== null) {
                            if (from >= 0 && to + 1 <= doc.content.size && from < to + 1) {
                                results.push({
                                    from,
                                    to: to + 1,
                                    text: candidate
                                });
                            }
                        }
                        
                        i += candidate.length - 1;
                    }
                }
            }
        }

        return results;
    }, [editor, caseSensitive, useRegex]);

    // Update matches when find text, case sensitivity, or regex mode changes
    useEffect(() => {
        if (findText.trim() && editor) {
            const newMatches = findMatches(findText);
            setMatches(newMatches);
            setCurrentMatchIndex(newMatches.length > 0 ? 0 : -1);
        } else {
            setMatches([]);
            setCurrentMatchIndex(-1);
            setRegexError(null);
        }
    }, [findText, findMatches, editor]);

    // Update highlights using the extension
    useEffect(() => {
        if (!editor || !isOpen) {
            clearFindReplaceMatches();
            // Force update to clear decorations
            if (editor) {
                const { view } = editor;
                if (view) {
                    const { state, dispatch } = view;
                    // Create a transaction with a meta to force plugin update
                    const tr = state.tr.setMeta('findReplaceUpdate', true);
                    dispatch(tr);
                }
            }
            return;
        }

        if (matches.length === 0) {
            clearFindReplaceMatches();
            // Force update to clear decorations
            const { view } = editor;
            if (view) {
                const { state, dispatch } = view;
                const tr = state.tr.setMeta('findReplaceUpdate', true);
                dispatch(tr);
            }
            return;
        }

        // Update global matches
        setFindReplaceMatches(matches, currentMatchIndex);
        
        // Force editor to update decorations by dispatching a transaction with meta
        const { view } = editor;
        if (view) {
            const { state, dispatch } = view;
            // Use a meta flag to trigger plugin update even if document hasn't changed
            const tr = state.tr.setMeta('findReplaceUpdate', true);
            dispatch(tr);
        }
    }, [matches, currentMatchIndex, editor, isOpen]);

    // Scroll to current match and set selection
    useEffect(() => {
        // Clear any pending scroll timeout
        if (scrollTimeoutRef.current !== null) {
            clearTimeout(scrollTimeoutRef.current);
            scrollTimeoutRef.current = null;
        }

        if (!editor || currentMatchIndex < 0 || matches.length === 0) {
            return;
        }

        const match = matches[currentMatchIndex];
        if (match) {
            try {
                // Set selection to the current match for better visibility
                editor.commands.setTextSelection({
                    from: match.from,
                    to: match.to
                });

                // Wait a bit for the selection to be set, then scroll
                scrollTimeoutRef.current = window.setTimeout(() => {
                    const { view } = editor;
                    if (!view) return;

                    // Find the scroll container by traversing up from the editor DOM
                    let scrollContainer: HTMLElement | null = null;
                    let element: HTMLElement | null = view.dom;
                    
                    while (element && !scrollContainer) {
                        const style = window.getComputedStyle(element);
                        if (style.overflow === 'auto' || style.overflowY === 'auto' || style.overflow === 'scroll' || style.overflowY === 'scroll') {
                            scrollContainer = element;
                            break;
                        }
                        element = element.parentElement;
                    }

                    // If no scroll container found, try finding the tiptap-editor-container's scrollable child
                    if (!scrollContainer) {
                        const container = view.dom.closest('.tiptap-editor-container');
                        if (container) {
                            // Find the direct child div that has overflow
                            const scrollableChild = Array.from(container.children).find((child) => {
                                const childEl = child as HTMLElement;
                                const style = window.getComputedStyle(childEl);
                                return style.overflow === 'auto' || style.overflowY === 'auto' || 
                                       style.overflow === 'scroll' || style.overflowY === 'scroll';
                            }) as HTMLElement;
                            if (scrollableChild) {
                                scrollContainer = scrollableChild;
                            }
                        }
                    }

                    if (scrollContainer) {
                        // Get coordinates of the match position
                        const coords = view.coordsAtPos(match.from);
                        const containerRect = scrollContainer.getBoundingClientRect();
                        
                        // Calculate scroll position to put the match at the top of the visible area
                        // Subtract container's top position and add a small offset for padding
                        const scrollTop = scrollContainer.scrollTop + (coords.top - containerRect.top) - 20;
                        
                        scrollContainer.scrollTo({
                            top: Math.max(0, scrollTop),
                            behavior: 'smooth'
                        });
                    }
                    
                    // Clear the timeout ref after execution
                    scrollTimeoutRef.current = null;
                }, 50);
            } catch (error) {
                console.error('Error scrolling to match:', error);
            }
        }

        // Cleanup function to clear timeout when effect re-runs or component unmounts
        return () => {
            if (scrollTimeoutRef.current !== null) {
                clearTimeout(scrollTimeoutRef.current);
                scrollTimeoutRef.current = null;
            }
        };
    }, [currentMatchIndex, matches, editor]);

    // Handle Find Next
    const handleFindNext = useCallback(() => {
        if (matches.length === 0) return;
        setCurrentMatchIndex((prev) => (prev + 1) % matches.length);
    }, [matches.length]);

    // Handle Find Previous
    const handleFindPrevious = useCallback(() => {
        if (matches.length === 0) return;
        setCurrentMatchIndex((prev) => (prev - 1 + matches.length) % matches.length);
    }, [matches.length]);

    // Handle Replace
    const handleReplace = () => {
        if (!editor || currentMatchIndex < 0 || matches.length === 0) return;

        const match = matches[currentMatchIndex];
        try {
            editor.commands.setTextSelection({
                from: match.from,
                to: match.to
            });
            
            // If using regex, apply regex replacement with capture groups
            let replacementText = replaceText;
            if (useRegex && findText) {
                try {
                    const flags = caseSensitive ? 'g' : 'gi';
                    const regex = new RegExp(findText, flags);
                    // Get the matched text from the document
                    const doc = editor.state.doc;
                    const matchedText = doc.textBetween(match.from, match.to);
                    // Apply regex replacement (supports $1, $2, etc. for capture groups)
                    replacementText = matchedText.replace(regex, replaceText);
                } catch (error) {
                    // If regex replacement fails, use plain text
                    console.warn('Regex replacement failed, using plain text:', error);
                }
            }
            
            editor.commands.insertContent(replacementText);
            
            // Recalculate matches after replacement
            const newMatches = findMatches(findText);
            setMatches(newMatches);
            
            // Adjust current match index
            if (newMatches.length > 0) {
                // Try to stay at the same position, or move to next match
                const newIndex = Math.min(currentMatchIndex, newMatches.length - 1);
                setCurrentMatchIndex(newIndex);
            } else {
                setCurrentMatchIndex(-1);
            }
        } catch (error) {
            console.error('Error replacing text:', error);
        }
    };

    // Handle Replace All - use useCallback to avoid stale closure issues
    const handleReplaceAll = useCallback(async () => {
        if (!editor) return;
        
        // Use ref to immediately prevent concurrent calls (synchronous check)
        if (isReplacingAllRef.current) return;
        isReplacingAllRef.current = true;

        // Capture current matches and other state at the start of the operation
        // This prevents issues if user changes search text during replacement
        const currentMatches = findMatches(findText);
        if (currentMatches.length === 0) {
            isReplacingAllRef.current = false;
            return;
        }

        // Set loading state for UI
        setIsReplacingAll(true);
        
        // Use double setTimeout to ensure React has time to render the loading state
        await new Promise<void>((resolve) => {
            setTimeout(() => {
                // Second timeout to ensure UI has updated
                setTimeout(async () => {
                    try {
                        // Use the captured matches, not the closure variable
                        // Sort matches by position in reverse order to avoid position shifting issues
                        const sortedMatches = [...currentMatches].sort((a, b) => b.from - a.from);
                        
                        // Pre-compute replacement texts for all matches using the original document
                        // This avoids position invalidation issues
                        const originalDoc = editor.state.doc;
                        const replacementTexts = sortedMatches.map((match) => {
                            let replacementText = replaceText;
                            if (useRegex && findText) {
                                try {
                                    const flags = caseSensitive ? 'g' : 'gi';
                                    const regex = new RegExp(findText, flags);
                                    // Get the matched text from the original document
                                    const matchedText = originalDoc.textBetween(match.from, match.to);
                                    // Apply regex replacement (supports $1, $2, etc. for capture groups)
                                    replacementText = matchedText.replace(regex, replaceText);
                                } catch (error) {
                                    // If regex replacement fails, use plain text
                                    console.warn('Regex replacement failed, using plain text:', error);
                                }
                            }
                            return replacementText;
                        });
                        
                        // Replace from end to beginning
                        // Since we're going backwards, positions before the current match remain valid
                        // For large replacements, yield to the browser every few operations
                        for (let i = 0; i < sortedMatches.length; i++) {
                            const match = sortedMatches[i];
                            const currentState = editor.state;
                            
                            // Verify positions are still valid in current document
                            if (match.from >= 0 && match.to <= currentState.doc.content.size && match.from < match.to) {
                                editor.commands.setTextSelection({
                                    from: match.from,
                                    to: match.to
                                });
                                
                                // Use the pre-computed replacement text
                                editor.commands.insertContent(replacementTexts[i]);
                            }
                            
                            // Yield to browser every 10 replacements to keep UI responsive
                            if (i % 10 === 0 && i > 0) {
                                await new Promise(resolve => setTimeout(resolve, 0));
                            }
                        }

                        // Clear matches after replace all
                        setMatches([]);
                        setCurrentMatchIndex(-1);
                        
                        resolve();
                    } catch (error) {
                        console.error('Error replacing all text:', error);
                        resolve();
                    } finally {
                        isReplacingAllRef.current = false;
                        setIsReplacingAll(false);
                    }
                }, 100); // Delay to ensure spinner renders
            }, 0);
        });
    }, [editor, findText, replaceText, useRegex, caseSensitive, findMatches]);

    // Handle keyboard shortcuts
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            // Escape to close
            if (e.key === 'Escape') {
                onClose();
                return;
            }

            // Enter in find field to find next
            if (e.target === findInputRef.current && e.key === 'Enter') {
                e.preventDefault();
                if (e.shiftKey) {
                    handleFindPrevious();
                } else {
                    handleFindNext();
                }
                return;
            }

            // F3 to find next, Shift+F3 to find previous
            if (e.key === 'F3') {
                e.preventDefault();
                if (e.shiftKey) {
                    handleFindPrevious();
                } else {
                    handleFindNext();
                }
                return;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose, handleFindNext, handleFindPrevious]);

    // Drag handlers
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        if (dialogRef.current) {
            isDraggingRef.current = true;
            const rect = dialogRef.current.getBoundingClientRect();
            dragStartPosRef.current = {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            };
            dialogPosRef.current = {
                x: rect.left,
                y: rect.top
            };
            e.preventDefault();
        }
    }, []);

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (isDraggingRef.current && dialogRef.current) {
            const newX = e.clientX - dragStartPosRef.current.x;
            const newY = e.clientY - dragStartPosRef.current.y;
            
            // Keep dialog within viewport
            const maxX = window.innerWidth - dialogRef.current.offsetWidth;
            const maxY = window.innerHeight - dialogRef.current.offsetHeight;
            
            const constrainedX = Math.max(0, Math.min(newX, maxX));
            const constrainedY = Math.max(0, Math.min(newY, maxY));
            
            dialogRef.current.style.left = `${constrainedX}px`;
            dialogRef.current.style.top = `${constrainedY}px`;
            dialogRef.current.style.transform = 'none';
        }
    }, []);

    const handleMouseUp = useCallback(() => {
        isDraggingRef.current = false;
    }, []);

    // Set up drag event listeners
    useEffect(() => {
        if (isOpen) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isOpen, handleMouseMove, handleMouseUp]);

    // Reset position when dialog opens
    useEffect(() => {
        if (isOpen && dialogRef.current) {
            // Reset to default centered position
            dialogRef.current.style.left = '';
            dialogRef.current.style.top = '';
            dialogRef.current.style.transform = '';
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="find-replace-dialog-overlay" onClick={onClose}>
            <div 
                ref={dialogRef}
                className="find-replace-dialog" 
                onClick={(e) => e.stopPropagation()}
                style={{ position: 'absolute' }}
            >
                <div 
                    className="find-replace-dialog-header"
                    onMouseDown={handleMouseDown}
                    style={{ cursor: 'move' }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <GripVertical size={16} style={{ color: '#999' }} />
                        <h3>Find and Replace</h3>
                    </div>
                    <button
                        className="find-replace-close-button"
                        onClick={onClose}
                        title="Close (Esc)"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="find-replace-dialog-content">
                    {/* Find Section */}
                    <div className="find-replace-section">
                        <label className="find-replace-label">
                            <Search size={16} />
                            Find
                        </label>
                        <div className="find-replace-input-container">
                            <input
                                ref={findInputRef}
                                type="text"
                                value={findText}
                                onChange={(e) => setFindText(e.target.value)}
                                placeholder={useRegex ? "Enter regex pattern..." : "Enter text to find..."}
                                className="find-replace-input"
                            />
                            {matches.length > 0 && (
                                <div className="find-replace-match-count">
                                    {currentMatchIndex + 1} of {matches.length}
                                </div>
                            )}
                        </div>
                        <div className="find-replace-controls">
                            <button
                                onClick={handleFindPrevious}
                                disabled={matches.length === 0}
                                className="find-replace-button"
                                title="Find Previous (Shift+Enter)"
                            >
                                ↑ Previous
                            </button>
                            <button
                                onClick={handleFindNext}
                                disabled={matches.length === 0}
                                className="find-replace-button"
                                title="Find Next (Enter or F3)"
                            >
                                Next ↓
                            </button>
                            <label className="find-replace-checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={caseSensitive}
                                    onChange={(e) => setCaseSensitive(e.target.checked)}
                                />
                                Match case
                            </label>
                            <label className="find-replace-checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={useRegex}
                                    onChange={(e) => setUseRegex(e.target.checked)}
                                />
                                Regex
                            </label>
                        </div>
                        {regexError && (
                            <div className="find-replace-error" style={{
                                marginTop: '8px',
                                padding: '6px 8px',
                                backgroundColor: '#fee2e2',
                                border: '1px solid #fca5a5',
                                borderRadius: '4px',
                                color: '#991b1b',
                                fontSize: '12px'
                            }}>
                                Regex error: {regexError}
                            </div>
                        )}
                    </div>

                    {/* Replace Section */}
                    <div className="find-replace-section">
                        <label className="find-replace-label">
                            <Replace size={16} />
                            Replace
                        </label>
                        <div className="find-replace-input-container">
                            <input
                                ref={replaceInputRef}
                                type="text"
                                value={replaceText}
                                onChange={(e) => setReplaceText(e.target.value)}
                                placeholder={useRegex ? "Enter replacement (use $1, $2 for groups)..." : "Enter replacement text..."}
                                className="find-replace-input"
                            />
                        </div>
                        <div className="find-replace-controls">
                            <button
                                onClick={handleReplace}
                                disabled={matches.length === 0 || currentMatchIndex < 0}
                                className="find-replace-button find-replace-button-primary"
                                title="Replace current match"
                            >
                                Replace
                            </button>
                            <button
                                onClick={handleReplaceAll}
                                disabled={matches.length === 0 || isReplacingAll}
                                className="find-replace-button find-replace-button-primary"
                                title="Replace all matches"
                            >
                                {isReplacingAll ? (
                                    <>
                                        <Loader2 size={14} className="spinning" style={{ marginRight: '6px', display: 'inline-block' }} />
                                        Replacing...
                                    </>
                                ) : (
                                    'Replace All'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FindReplaceDialog;

